import { createContext, useContext } from "react";

import PropTypes from "prop-types";

import { API_BASE_URL } from "@/api/api.config";
import { sanitizeQueryParamsForUser } from "@/assignments/utils/assignmentUtils";
import useRestQuery from "@/remoteState/useRestQuery";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import stringifyParams from "@/utils/stringifyParams";

const propTypes = {
  assignmentsQueryInitialValue: PropTypes.shape({
    data: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
  }),
  children: PropTypes.node,
};

const AssignmentsQueryContext = createContext({});

const useAssignments = () => {
  return useContext(AssignmentsQueryContext);
};

const useAssignmentsGet = (queryParams = {}, skip = false) => {
  const { isTeacher } = useGetUserProperties();
  let resource = `${API_BASE_URL}/api/v3/assignment/`;

  const params = sanitizeQueryParamsForUser(
    {
      include_drafts: true,
      excluded_roles: "district_admin,principal",
      ...queryParams,
    },
    isTeacher,
  );

  const queryString = stringifyParams(params);
  resource = `${resource}${queryString}`;
  return useRestQuery(resource, { skip });
};

const AssignmentsProvider = ({
  assignmentsQueryInitialValue = {
    data: undefined,
    loading: false,
    error: undefined,
  },
  children,
}) => {
  // we only want to make the request for assignments if the user is not a student
  // necessary for unblocking OCPS CAT scenario (all content tagged by default)
  const { isStudent } = useGetUserProperties();

  const assignmentsRequest = useAssignmentsGet({}, isStudent);

  const { data, error, loading } = useAssignmentsGet({}, isStudent);

  const contextData =
    data !== undefined && !error
      ? assignmentsRequest
      : { ...assignmentsQueryInitialValue, loading };

  return (
    <AssignmentsQueryContext.Provider value={contextData}>
      {children}
    </AssignmentsQueryContext.Provider>
  );
};

AssignmentsProvider.propTypes = propTypes;

export default useAssignments;
export { AssignmentsProvider, AssignmentsQueryContext, useAssignmentsGet };
